<template>
  <div>
    <v-dialog v-model="show" :max-width="csvDialogMaxWidth">
      <v-card>
        <v-card-title>
          <span class="text-h5 font-weight-bold">CSVインポート</span>
        </v-card-title>
        <v-card-text class="mt-4 mb-n6">
          <v-file-input
            v-model="csvFile" label="CSVファイルを選択" accept=".csv" dense outlined :key="inputKey" @change="inputCsv"
            :success-messages="csvFile && csvDataErrMsg.length == 0 ? 'ファイルの読み込みが完了しました。' : ''"
            :error-messages="csvDataErrMsg.length != 0 ? 'ファイルの読み込みに失敗しました。エラーが' + csvDataErrMsg.length + '件発生しています。' : ''"
          ></v-file-input>
          <div v-if="csvDataErrMsg.length != 0" max-height="200px" class="mb-6" style="max-height: 300px; overflow-y: auto;">
            <div v-for="(data, index) in csvDataErrMsg" :key="index">
              <v-alert dense text color="error" icon="mdi-alert-outline">{{ data }}</v-alert>
            </div>
          </div>
          <v-alert v-if="csvFile && csvDataErrMsg.length == 0" dense text color="success" icon="mdi-information-outline">
            データのインポートが完了すると、登録された情報を含むZIPファイルが自動的にダウンロードされます。
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn :loading="loading" color="green" dark @click="show = false" outlined>キャンセル</v-btn>
          <v-btn :disabled="csvDataErrMsg.length != 0 || !csvFile" :loading="loading" color="green white--text"
            @click="sendCsv()">保存</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Papa from "papaparse";
import Vue from "vue";
import { mapActions, mapMutations, mapState } from "vuex";
import {
  UPLOAD_MULTI_STAFF,
} from "@/store/action-types";
import {
  REQUIRED_ERROR_MSG,
  MAX_LENGTH_255_ERROR_MSG,
  FULL_WIDTH_10_ERROR_MSG,
  CSV_FILE_ERROR_MSG,
} from "@/constants";

export default Vue.extend({
  name: 'StaffCsvImport',
  props: {
    value: {
      type: Boolean,
      required: true
    },
    gradeAll: Array,
    classroomAll: Array,
  },
  data() {
    return {
      loading: false,
      csvFile: null,
      csvData: [],
      csvDataErrMsg: [],
      staffCsv: null,
      inputKey: 0,
    }
  },
  computed: {
    show: {
      get() {
        return this.value;
      },
      set(newVal) {
        this.$emit("input", newVal);
      }
    },
    csvDialogMaxWidth() {
      return "600px";
    },
  },
  methods: {
    ...mapActions({
      uploadMultiStaff: UPLOAD_MULTI_STAFF,
    }),
    async inputCsv() {
      this.inputKey += 1; // 同じファイルを再選択した場合でも@changeを発火させる
      this.csvDataErrMsg = [];
      const file = this.csvFile;
      if (file) {
        const reader = new FileReader();
        reader.readAsText(file, 'Shift-JIS'); // Shift-JIS形式で読み込む
        reader.onload = async () => {
          const csv = reader.result;
          const results = Papa.parse(csv, { header: true });
          this.csvData = this.csvDataKeyChange(results.data);
          this.convertGradeIdType(); // Excelで保存した際に'01'が数値の1に変換されてしまう事象に対応
          this.convertClassIdType(); // Excelで保存した際に'01'が数値の1に変換されてしまう事象に対応
          this.csvDataValidateCheck();
        };
      }
    },
    convertGradeIdType() {
      // (数値,文字列,0埋め文字列)を0埋め文字列に変換
      for (let i = 0; i < this.csvData.length; i++) {
        const managed_grade = this.csvData[i].managed_grade;
        let gradeId = '';
        if(managed_grade != undefined && managed_grade != '' && managed_grade != null){
          gradeId = String(managed_grade).padStart(2, '0');
        }
        this.csvData[i].managed_grade = gradeId;
      }
    },
    convertClassIdType() {
      // (数値,文字列,0埋め文字列)を0埋め文字列に変換
      for (let i = 0; i < this.csvData.length; i++) {
        const managed_class = this.csvData[i].managed_class;
        let classId = '';
        if(managed_class != undefined && managed_class != '' && managed_class != null){
          classId = String(managed_class).padStart(2, '0');
        }
        this.csvData[i].managed_class = classId;
      }
    },
    csvDataValidateCheck() {
      let deleteRowIndexList = [];
      let importCount = 0;
      if (!this.csvData || this.csvData.length === 0) {
        this.csvDataErrMsg.push(CSV_FILE_ERROR_MSG);
        return;
      }
      for (let i = 0; i < this.csvData.length; i++) {
        const data = this.csvData[i];
        if (this.deleteNullOrEmptyRow(data)) {
          deleteRowIndexList.push(i);
        } else {
          this.checkLastNameValidate(data, i + 2);
          this.checkFirstNameValidate(data, i + 2);
          this.checkMailAddressValidate(data, i + 2);
          this.checkAuthorityValidate(data, i + 2);
          this.checkManagedGradeValidate(data, i + 2);
          this.checkManagedClassValidate(data, i + 2);
          this.checkMemoValidate(data, i + 2);
          this.checkBusDriverValidate(data, i + 2);
          importCount++;
        }
      }
      for (let i = 0; i < deleteRowIndexList.length; i++) {
        this.csvData.splice(deleteRowIndexList[i] - i, 1);
      }
      if (importCount == 0) {
        this.csvDataErrMsg.push(CSV_FILE_ERROR_MSG);
      }
    },
    checkLastNameValidate(data, i) {
      // 姓のValidation
      const posErrMsg = "行目でエラー : ";
      const emptyErrMsg = "姓" + REQUIRED_ERROR_MSG;
      const lengthErrMsg = "姓は" + FULL_WIDTH_10_ERROR_MSG;

      const validateTarget = data.last_name;

      if (validateTarget == null || validateTarget == "") {
        this.csvDataErrMsg.push(i + posErrMsg + emptyErrMsg);
      } else if (validateTarget.length > 10) {
        this.csvDataErrMsg.push(i + posErrMsg + lengthErrMsg);
      } else if (!/* eslint no-control-regex: off */ /[^\x01-\x7E\uFF61-\uFF9F]+$/.test(validateTarget)) {
        this.csvDataErrMsg.push(i + posErrMsg + lengthErrMsg);
      }
    },
    checkFirstNameValidate(data, i) {
      // 名のValidation
      const posErrMsg = "行目でエラー : ";
      const emptyErrMsg = "名" + REQUIRED_ERROR_MSG;
      const lengthErrMsg = "名は" + FULL_WIDTH_10_ERROR_MSG;

      const validateTarget = data.first_name;

      if (validateTarget == null || validateTarget == "") {
        this.csvDataErrMsg.push(i + posErrMsg + emptyErrMsg);
      } else if (validateTarget.length > 10) {
        this.csvDataErrMsg.push(i + posErrMsg + lengthErrMsg);
      } else if (!/* eslint no-control-regex: off */ /[^\x01-\x7E\uFF61-\uFF9F]+$/.test(validateTarget)) {
        this.csvDataErrMsg.push(i + posErrMsg + lengthErrMsg);
      }
    },
    checkMailAddressValidate(data, i) {
      // メールアドレスのValidation
      const posErrMsg = "行目でエラー : ";
      const lengthErrMsg = "メールアドレスは" + MAX_LENGTH_255_ERROR_MSG;
      const mailErrMsg = "メールアドレスの形式が正しくありません。";

      const validateTarget = data.mail;
      const mailAddressRule = /^[a-zA-Z0-9_.+-]+@([a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.)+[a-zA-Z]{2,}$/.test(validateTarget);

      if (validateTarget == null || validateTarget == "") {
        return;
      } else if (validateTarget.length > 255) {
        this.csvDataErrMsg.push(i + posErrMsg + lengthErrMsg);
      } else if (validateTarget !== "" && !mailAddressRule) {
        this.csvDataErrMsg.push(i + posErrMsg + mailErrMsg);
      }
    },
    checkAuthorityValidate(data, i) {
      // 権限のValidation
      const posErrMsg = "行目でエラー : ";
      const emptyErrMsg = "権限" + REQUIRED_ERROR_MSG;
      const adminErrMsg = "全権管理者はインポートで登録することができません。";
      const outAuthErrMsg = "権限の入力形式が誤っています。";

      const validateTarget = data.authority;

      if (validateTarget == null || validateTarget == "") {
        this.csvDataErrMsg.push(i + posErrMsg + emptyErrMsg);
      } else if (validateTarget == "1") {
        this.csvDataErrMsg.push(i + posErrMsg + adminErrMsg);
      } else if (
        (validateTarget != "2") && (validateTarget != "3") &&
        (validateTarget != "4") && (validateTarget != "5")
      ) {
        this.csvDataErrMsg.push(i + posErrMsg + outAuthErrMsg);
      }
    },
    checkManagedGradeValidate(data, i) {
      // 担当学年のValidation
      const posErrMsg = "行目でエラー : ";
      const emptyErrMsgGakunen = "学年主任の場合、学年" + REQUIRED_ERROR_MSG;
      const emptyErrMsgTannnin = "担任の場合、学年" + REQUIRED_ERROR_MSG;
      const wrongAreaErrMsg = "担当学年の入力形式が誤っています。";
      const outAreaErrMsg = "存在しない担当学年が指定されています。";

      const validateTarget = data.managed_grade;
      const containGrade = this.gradeAll.some(item => item.grade_id == validateTarget.padStart(2, "0"));

      if (data.authority == "3" && (validateTarget == null || validateTarget == "")) {
        this.csvDataErrMsg.push(i + posErrMsg + emptyErrMsgGakunen);
      } else if (data.authority == "4" && (validateTarget == null || validateTarget == "")) {
        this.csvDataErrMsg.push(i + posErrMsg + emptyErrMsgTannnin);
      } else if ((data.authority == "3") || (data.authority == "4")) {
        if (validateTarget !== null && isNaN(Number(validateTarget))) {
          this.csvDataErrMsg.push(i + posErrMsg + wrongAreaErrMsg);
        } else if (!containGrade) {
          this.csvDataErrMsg.push(i + posErrMsg + outAreaErrMsg);
        }
      }
    },
    checkManagedClassValidate(data, i) {
      // 担当クラスのValidation
      const posErrMsg = "行目でエラー : ";
      const emptyErrMsg = "担任の場合、クラス" + REQUIRED_ERROR_MSG;
      const wrongAreaErrMsg = "担当クラスの入力形式が誤っています。";
      const outAreaErrMsg = "存在しない担当クラスが指定されています。";

      const validateTarget = data.managed_class;
      const findGrade = this.classroomAll.filter(item => item.grade_id == data.managed_grade.padStart(2, "0"));

      let containClass = "";
      if (findGrade != null && findGrade != "" && findGrade != undefined) {
        containClass = findGrade.some(item => item.class_id == validateTarget.padStart(2, "0"));
      }

      if (data.authority == "4") {
        if (validateTarget == null || validateTarget == "") {
          this.csvDataErrMsg.push(i + posErrMsg + emptyErrMsg);
        } else if (isNaN(Number(validateTarget))) {
          this.csvDataErrMsg.push(i + posErrMsg + wrongAreaErrMsg);
        } else if (!containClass && data.managed_grade) {
          this.csvDataErrMsg.push(i + posErrMsg + outAreaErrMsg);
        }
      }
    },
    checkMemoValidate(data, i) {
      // メモのValidation
      const posErrMsg = "行目でエラー : ";
      const lengthErrMsg = "メモは" + MAX_LENGTH_255_ERROR_MSG;

      const validateTarget = data.memo;

      if (validateTarget == null || validateTarget == "") {
        return;
      } else if (validateTarget.length > 255) {
        this.csvDataErrMsg.push(i + posErrMsg + lengthErrMsg);
      }
    },
    checkBusDriverValidate(data, i) {
      // バス運転手のValidation
      const posErrMsg = "行目でエラー : ";
      const outBusErrMsg = "バス運転手の入力形式が誤っています。";

      const validateTarget = data.is_driver;

      if ((validateTarget != "0") && (validateTarget != "1")) {
        this.csvDataErrMsg.push(i + posErrMsg + outBusErrMsg);
      }
    },
    deleteNullOrEmptyRow(csvData) {
      if (
        (csvData.last_name == "" || !csvData.last_name) &&
        (csvData.first_name == "" || !csvData.first_name)
      ) {
        if (csvData.authority == "" || !csvData.authority) {
          return true;
        } else if (
          csvData.authority == "3" &&
          (csvData.managed_grade == "" || !csvData.managed_grade)
        ) {
          return true;
        } else if (
          csvData.authority == "4" &&
          (csvData.managed_grade == "" || !csvData.managed_grade) &&
          (csvData.managed_class == "" || !csvData.managed_class)
        ) {
          return true;
        }
      }
      return false;
    },
    csvDataKeyChange(csvData) {
      // csvDataのキー名を日本語から英語に変える 
      for (let i = 0; i < csvData.length; i++) {
        csvData[i].last_name = csvData[i]['職員氏名（姓）'];
        delete csvData[i]['職員氏名（姓）'];
        
        csvData[i].first_name = csvData[i]['職員氏名（名）'];
        delete csvData[i]['職員氏名（名）'];

        csvData[i].mail = csvData[i]['メールアドレス'];
        delete csvData[i]['メールアドレス'];

        csvData[i].managed_grade = csvData[i]['担当学年'];
        delete csvData[i]['担当学年'];
        
        csvData[i].managed_class = csvData[i]['担当クラス'];
        delete csvData[i]['担当クラス'];

        csvData[i].memo = csvData[i]['メモ'];
        delete csvData[i]['メモ'];
        
        csvData[i].authority = csvData[i]['権限'];
        delete csvData[i]['権限'];

        csvData[i].is_driver = csvData[i]['バス運転手'];
        delete csvData[i]['バス運転手'];
      }
      return csvData;
    },
    async sendCsv() {
      this.loading = true;
      let index = 0;
      const chunkSize = 50;
      this.loadingDialogText = '職員情報を登録中です。';
      this.allDownloadingLength = '%';
      let staffList = [];
      let res = null;
      while (index < this.csvData.length) {
        this.currentDownloadingClass = Math.floor(((index == 0 ? 1 : index) / this.csvData.length) * 100);
        let chunk = this.csvData.slice(index, index + chunkSize);
        res = await this.uploadMultiStaff(chunk);
        staffList = staffList.concat(res.body);
        index += chunkSize;
      }
      this.loadingDialogText = '';
      this.allDownloadingLength = '';
      this.currentDownloadingClass = '';
      this.show = false;
      this.$emit('staffCsvImport', staffList);
    },
  },
});
</script>